/**
 * ================================================================================================
 *  THE ICONS THE APPROVED DESIGN USES — DRAWN HERE, NOT FETCHED.
 * ================================================================================================
 *
 * The approved prototypes mark every icon with `data-lucide="<name>"` and call `lucide.createIcons()`
 * from a CDN script. That is right for a prototype and wrong for this product, for three reasons
 * that are worth stating because the result is a visible deviation from the kit:
 *
 *   1. A CDN script is a third-party execution dependency on every page load of an accounting
 *      product. The estate is a closed VPN deployment; an icon font that cannot be reached would
 *      render the whole navigation as empty boxes.
 *   2. Adding `lucide-react` is a dependency decision with its own supply-chain review, and this
 *      change is a UI change.
 *   3. The bytes are tiny. The glyphs below, at this size, cost less than the fetch would. (This
 *      line used to count them -- "nineteen" -- and was wrong by one before anybody added the
 *      next. `ICON_NAMES` is the count; a number written beside it is a second one to maintain.)
 *
 * So the glyphs are drawn here, inline, in the same 24-unit box and the same 2px stroke weight the
 * reference uses, and they are referenced BY THE PROTOTYPE'S OWN NAMES so the correspondence can be
 * checked line by line against the kit rather than guessed at. They are not pixel copies of Lucide
 * and are not claimed to be: see the difference log.
 *
 * `aria-hidden` on every one. Each icon in this product sits beside its own text label — an icon
 * that announced itself would make a screen reader say the name twice.
 */

export const ICON_NAMES = [
  'house',
  'users',
  'user-check',
  'user-plus',
  'user-minus',
  'layers',
  'credit-card',
  'package',
  'plug',
  'shield',
  'activity',
  'settings',
  'flask-conical',
  'repeat',
  'chart-column',
  'trending-down',
  'mail',
  'database',
  'building',
  'tag',
  'calendar',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

/** The drawing of each glyph, inside a 24×24 box, stroked rather than filled. */
const PATHS: Readonly<Record<IconName, JSX.Element>> = {
  /*
   * THE FILING-DATES CARD, 0030/0031. A calendar rather than a clock: these are DATES in a period
   * -- the 15th, the 16th, the 19th of the month after -- and not durations.
   */
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4" />
      <path d="M3 10h18" />
    </>
  ),
  house: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M10 20v-6h4v6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5.4 6-5.4s6 2.1 6 5.4" />
      <path d="M16 5.2A3.2 3.2 0 0 1 16 11" />
      <path d="M17.5 14.9c2.1.6 3.5 2.4 3.5 5.1" />
    </>
  ),
  'user-check': (
    <>
      <circle cx="9.5" cy="8" r="3.2" />
      <path d="M3.5 20c0-3.3 2.7-5.4 6-5.4 1.2 0 2.3.3 3.2.8" />
      <path d="m15 16.5 2.2 2.2 3.8-4" />
    </>
  ),
  'user-plus': (
    <>
      <circle cx="9.5" cy="8" r="3.2" />
      <path d="M3.5 20c0-3.3 2.7-5.4 6-5.4 1.2 0 2.3.3 3.2.8" />
      <path d="M18 14v6" />
      <path d="M15 17h6" />
    </>
  ),
  'user-minus': (
    <>
      <circle cx="9.5" cy="8" r="3.2" />
      <path d="M3.5 20c0-3.3 2.7-5.4 6-5.4 1.2 0 2.3.3 3.2.8" />
      <path d="M15 17h6" />
    </>
  ),
  layers: (
    <>
      <path d="m12 3 8.5 4.5L12 12 3.5 7.5 12 3Z" />
      <path d="m3.5 12.5 8.5 4.5 8.5-4.5" />
      <path d="m3.5 17 8.5 4.5 8.5-4.5" />
    </>
  ),
  'credit-card': (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 10h19" />
      <path d="M6 15h4" />
    </>
  ),
  package: (
    <>
      <path d="m12 3 8.5 4.3v9.4L12 21l-8.5-4.3V7.3L12 3Z" />
      <path d="m3.5 7.3 8.5 4.4 8.5-4.4" />
      <path d="M12 11.7V21" />
    </>
  ),
  plug: (
    <>
      <path d="M9 3v6" />
      <path d="M15 3v6" />
      <path d="M6.5 9h11v3a5.5 5.5 0 0 1-11 0V9Z" />
      <path d="M12 17.5V21" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 5 6v6c0 4.2 2.9 7.5 7 9 4.1-1.5 7-4.8 7-9V6l-7-3Z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </>
  ),
  activity: (
    <>
      <path d="M3 12h4l2.5-7 5 14L17 12h4" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4 5.3 5.3" />
    </>
  ),
  'flask-conical': (
    <>
      <path d="M9.5 3v6.2L4.6 18a2 2 0 0 0 1.7 3h11.4a2 2 0 0 0 1.7-3l-4.9-8.8V3" />
      <path d="M8.5 3h7" />
      <path d="M7.2 14.5h9.6" />
    </>
  ),
  repeat: (
    <>
      <path d="M4 9a4 4 0 0 1 4-4h11" />
      <path d="m16 2 3 3-3 3" />
      <path d="M20 15a4 4 0 0 1-4 4H5" />
      <path d="m8 22-3-3 3-3" />
    </>
  ),
  'chart-column': (
    <>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <rect x="7.5" y="12" width="3" height="5" />
      <rect x="13" y="8" width="3" height="9" />
      <rect x="18" y="14" width="2.5" height="3" />
    </>
  ),
  'trending-down': (
    <>
      <path d="M3 7.5 10 14l3.5-3.5L21 18" />
      <path d="M21 13v5h-5" />
    </>
  ),
  mail: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="6" rx="7.5" ry="3" />
      <path d="M4.5 6v12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3V6" />
      <path d="M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
    </>
  ),
  building: (
    <>
      <path d="M4.5 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16" />
      <path d="M15.5 10h3a2 2 0 0 1 2 2v9" />
      <path d="M3 21h18" />
      <path d="M8 7h4M8 11h4M8 15h4" />
    </>
  ),
  tag: (
    <>
      <path d="M11 3H4.5A1.5 1.5 0 0 0 3 4.5V11l9.5 9.5a1.5 1.5 0 0 0 2.1 0l6-6a1.5 1.5 0 0 0 0-2.1L11 3Z" />
      <circle cx="7.5" cy="7.5" r="1.3" />
    </>
  ),
};

export interface IconProps {
  readonly name: IconName;
  /** A class so size and colour come from the surrounding rule, exactly as the reference does it. */
  readonly className?: string;
}

export function Icon({ name, className }: IconProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
